import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { renameWorkspace } from "../lib/api/index.js";
import { initialsOf } from "../lib/initials.js";
import { queryKeys } from "../lib/query-keys.js";
import "./rail-workspace.css";
import { useToast } from "./toast.js";

/**
 * Which tenant these numbers belong to, at the head of the workspace menu.
 *
 * The workspace's initials, its name and how many brands it measures
 * (DESIGN.md §5, "The workspace menu"). The name is a button that renames the
 * workspace in place; switching to another is the menu's foot.
 */
export function RailWorkspace({
  workspaceId,
  workspaceName,
  reportCount,
}: {
  readonly workspaceId: string;
  readonly workspaceName: string;
  readonly reportCount: number;
}) {
  const cache = useQueryClient();
  const toast = useToast();
  /*
   * Renaming the workspace, in place.
   *
   * There was no way to do it at all: a workspace took its name from whatever
   * the first account typed at sign-up and kept it forever. In place rather
   * than on a settings screen, because it is one field and the name is already
   * here — a screen to change one label is a screen nobody finds.
   */
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(workspaceName);

  const commit = async (): Promise<void> => {
    const name = draft.trim();
    setEditing(false);
    // Unchanged or emptied is a cancel, not a save. An empty workspace name
    // would leave the rail with a blank line and a lone initial.
    if (name === "" || name === workspaceName) {
      setDraft(workspaceName);
      return;
    }
    try {
      await renameWorkspace(workspaceId, name);
      // The name lives in the session payload, so that is what is re-read.
      await cache.invalidateQueries({ queryKey: queryKeys.me() });
      toast.done(`Workspace renamed to ${name}`);
    } catch (error) {
      // Put the old name back rather than leaving a rename that did not happen
      // on screen looking as though it did (§1) — and say so, or the reader
      // only sees their edit quietly undone.
      setDraft(workspaceName);
      toast.failed("Could not rename the workspace. The old name is back.", error);
    }
  };

  return (
    <div className="rail-workspace">
      {/* The name sits beside it, so a screen reader hears it once. */}
      <span className="rail-workspace-avatar" aria-hidden="true">
        {initialsOf(workspaceName)}
      </span>
      <span className="rail-workspace-text">
        {editing ? (
          <input
            className="rail-workspace-input"
            aria-label="Workspace name"
            value={draft}
            autoFocus
            maxLength={120}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            onBlur={() => {
              void commit();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") void commit();
              // Escape restores rather than saves. A rename is not something
              // to be committed by looking away from it.
              if (event.key === "Escape") {
                setDraft(workspaceName);
                setEditing(false);
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="rail-workspace-name"
            title="Rename this workspace"
            onClick={() => {
              setDraft(workspaceName);
              setEditing(true);
            }}
          >
            {workspaceName}
          </button>
        )}
        <span className="rail-workspace-meta">
          {reportCount === 1 ? "1 brand" : `${String(reportCount)} brands`}
        </span>
      </span>
    </div>
  );
}
